import { Paper, Typography, Box, List, ListItem, Fade } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';

const CompareResults = ({
    show,
    similarity,
    text1Analytics,
    text2Analytics,
    text1Sentiment,
    text2Sentiment,
    text1TopWords,
    text2TopWords,
}) => {
    return (
        <Fade in={show}>
            <Paper
                elevation={4}
                sx={{ margin: '20px auto', maxWidth: '100%', border: '2px solid #1f2641' }}
            >
                <Box
                    sx={{
                        backgroundColor: '#1f2641',
                        padding: '10px',
                        borderBottom: '2px solid #1f2641',
                    }}
                >
                    <Typography variant='h6' sx={{ color: 'white', fontWeight: 'bold' }}>
                        Comparison Results
                    </Typography>
                </Box>

                <Box sx={{ padding: '20px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                            Similarity:
                        </Typography>
                        <Typography
                            variant='h6'
                            sx={{ fontWeight: 'bold', fontSize: '1.7rem', marginLeft: '8px' }}
                        >
                            {(similarity * 100).toFixed(2)}%
                        </Typography>
                    </Box>
                    <Typography variant='body2'>
                        Indicates how similar the texts are based on their content.
                    </Typography>

                    <Box mt={2}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                            Word Count
                        </Typography>
                        <Typography variant='body2'>
                            This represents the total number of words in each text.
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 1: {text1Analytics.wordCount}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 2: {text2Analytics.wordCount}
                        </Typography>
                    </Box>

                    <Box mt={2}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                            Character Count
                        </Typography>
                        <Typography variant='body2'>
                            This is the total number of characters present in each text, including
                            spaces.
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 1: {text1Analytics.characterCount}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 2: {text2Analytics.characterCount}
                        </Typography>
                    </Box>

                    <Box mt={2}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                            Average Word Length
                        </Typography>
                        <Typography variant='body2'>
                            Reflects the average length of words in each text, indicating word
                            complexity.
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 1: {text1Analytics.averageWordLength.toFixed(2)}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 2: {text2Analytics.averageWordLength.toFixed(2)}
                        </Typography>
                    </Box>

                    <Box mt={2}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                            Readability Score
                        </Typography>
                        <Typography variant='body2'>
                            Higher scores indicate easier readability, based on the Flesch-Kincaid
                            scale.
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 1: {text1Analytics.readabilityScore}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 2: {text2Analytics.readabilityScore}
                        </Typography>
                    </Box>

                    <Box mt={2}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                            Sentiment Score
                        </Typography>
                        <Typography variant='body2'>
                            Indicates the overall emotional tone, with positive, negative, or
                            neutral sentiments.
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 1: {text1Sentiment.score}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Text 2: {text2Sentiment.score}
                        </Typography>
                    </Box>

                    <Box mt={2}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                            Top 3 Repeated Words
                            <Tooltip title="Excluded words: 'I', 'the', 'to', 'and', 'was', 'a', 'of', 'my', 'me', 'that'">
                                <InfoIcon sx={{ color: '#30bced', ml: 1, fontSize: '1rm' }} />
                            </Tooltip>
                        </Typography>
                        <Box display='flex' justifyContent='space-between'>
                            <Box flex={1}>
                                <Typography
                                    variant='subtitle1'
                                    gutterBottom
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {' '}
                                    Text 1
                                </Typography>
                                <List>
                                    {text1TopWords.map((word, index) => (
                                        <ListItem key={index}>
                                            <Typography variant='body2'>
                                                {word.word} ({word.frequency} times)
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>

                            <Box flex={1}>
                                <Typography
                                    variant='subtitle1'
                                    gutterBottom
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Text 2
                                </Typography>
                                <List>
                                    {text2TopWords.map((word, index) => (
                                        <ListItem key={index}>
                                            <Typography variant='body2'>
                                                {word.word} ({word.frequency} times)
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Fade>
    );
};

export default CompareResults;
